/**
 * 手动资产 / 手动负债契约 —— `net_worth_items` 的**唯一写入面**（批 `107` · K1）
 *
 * 依据：`DR-030`（载体定稿 · **禁录投资理财类** · **不得与 `major_assets` 双计**）·
 * J 域 PRD **§4.1.1 第 8 类**（手动资产）· **§4.1.2 第 7 类**（手动负债）·
 * 规格 **§12** · 架构 **§4.2 D 段**（`net_worth_items` 第 37 张表）。
 *
 * ## 🔴 这张表早就建好了，缺的只是"能写"（铁律 53：没入口 = 没兑现）
 *
 * `net_worth_items` 自建库起就在（`db/schema/d-asset-liability.ts`），
 * `AssetService.valuationSources()` 也一直把它读进 `composeNetWorth()` ——
 * 但**全项目零写路由** ⇒ 用户无法录入"借出去的钱 / 押金 / 收藏品"，
 * 净资产长期偏低且**无法自证**（用户看到的是一个他不认识的数）。
 *
 * ## 🔴 三条红线（都来自 `DR-030`，写在这里是为了让契约层"不容许"它们发生）
 *
 * | 红线 | 契约层怎么落实 |
 * |---|---|
 * | **禁录「投资理财」类** | `kind` **只有两个取值**（`asset` / `liability`）—— 根本没有"类别"字段可填，投资理财走 `holdings` / 投资页。刻意**不**加 `category` 字段：一旦有了它，"哪些类别不许选"就变成第二处判断（端侧筛一次、服务端查一次，必然漂移） |
 * | **不得与 `major_assets` 双计** | 契约层无法判断"这叫不叫同一套房"（名称不是判据）⇒ 由**表单文案**提示「房产 / 车辆请记在『重大资产』」，**只提示不拦截**（用户可能真有同名两件东西） |
 * | **`amount` 是事实值** | 不受规格 §22.2「绝不持久化汇总值」约束 —— 它无法从任何腿重算，只能由用户输入 |
 *
 * ## 为什么 `amount` 允许 `0`
 *
 * 「押金交了但金额待定」是真实场景，而 `0` 与"没填"在本表**不需要区分**
 * （`hidden` 才是"先不显示"的开关）。契约只禁负数：负数会让净资产方向反转，
 * 而方向已由 `kind` 表达 —— 两处都能表达方向 = 两处都能错。
 */

import { z } from 'zod';
import type { Cents } from '../types/brand.js';

// ───────────────────────────── 枚举 ─────────────────────────────

/**
 * 手动项的**两侧**。
 *
 * 🔴 与 schema 的 `kind` 联合类型逐值一致（`d-asset-liability.ts` 的
 * `text('kind').$type<'asset' | 'liability'>()`）—— 两处取值必须同源，
 * 故这里用 `as const` 对象 + 派生类型，而不是各写一遍字面量（铁律 10 同型）。
 */
export const NetWorthItemKind = {
  /** 资产方第 8 类：手动资产 */
  Asset: 'asset',
  /** 负债方第 7 类：手动负债 */
  Liability: 'liability',
} as const;
export type NetWorthItemKind = (typeof NetWorthItemKind)[keyof typeof NetWorthItemKind];

/** 合法取值的**字面量元组** —— 由 {@link NetWorthItemKind} 派生（不另抄一份） */
const KIND_VALUES = [NetWorthItemKind.Asset, NetWorthItemKind.Liability] as const;

// ───────────────────────────── 视图 ─────────────────────────────

/**
 * 一条手动资产 / 负债（列表与写入的返回同形）。
 *
 * 🔴 **不含任何折算值**：`amount` 是**原币**整数分（`DR-004`：不折算、不跨币相加）。
 * 折成本币只发生在 `composeNetWorth()` 里 —— 端侧若自己也折一次，
 * 就会出现"资产页与首页净资产差几块钱"（铁律 24 的现场）。
 */
export interface NetWorthItemView {
  id: string;
  kind: NetWorthItemKind;
  name: string;
  /** 金额（**原币整数分**） */
  amount: Cents;
  /** ISO 4217 */
  currency: string;
  /** 归属人（`users.id`） */
  ownerId: string;
  /** 归属人展示名 —— 服务端 join 好下发，端侧零 join */
  ownerName: string;
  /** 隐藏 ⇒ **不计入净资产**（`composeNetWorth` 侧过滤，不是端侧过滤） */
  hidden: boolean;
  /** 同侧内排序（升序；相同则按创建时间） */
  sort: number;
  note: string | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * `GET /api/net-worth-items` 的响应。
 *
 * ⚠️ **不分页**：手动项是"押金 / 借出 / 收藏品"这类**有界小集合**
 * （与账户、目标同因）—— 分页会让"两侧合计"变成需要翻页才能算对的东西。
 */
export interface NetWorthItemListResult {
  items: readonly NetWorthItemView[];
  /** = `items.length` */
  total: number;
  /** 账本本币（新建表单的默认币种；端侧不自己取） */
  baseCurrency: string;
}

/** `DELETE /api/net-worth-items/:id` 的返回 */
export interface NetWorthItemDeletedView {
  id: string;
}

// ───────────────────────────── 请求 ─────────────────────────────

/** 名称：1–40 字（与重大资产同名长上限，避免两处表单宽严不一） */
const ItemNameSchema = z
  .string()
  .trim()
  .min(1, '请填写名称')
  .max(40, '名称不超过 40 个字');

/** 币种：三位大写字母（ISO 4217 形态）。**存在性**由服务端判（币种表在汇率域） */
const CurrencySchema = z
  .string()
  .trim()
  .regex(/^[A-Z]{3}$/, '币种必须是三位大写字母（如 CNY / USD）');

/**
 * `POST /api/net-worth-items` —— 新增一条手动资产 / 负债。
 *
 * 🔴 `currency` 省略 ⇒ 服务端取**账本本币**（不是硬编码 `CNY`）。
 */
export const CreateNetWorthItemRequestSchema = z.object({
  kind: z.enum(KIND_VALUES),
  name: ItemNameSchema,
  /** 金额（整数分，≥ 0 —— 方向由 `kind` 表达，不用负号） */
  amount: z.number().int('金额必须是整数分').nonnegative('金额不得为负'),
  currency: CurrencySchema.optional(),
  /** 归属人；省略 ⇒ 当前登录用户 */
  ownerId: z.string().trim().min(1).optional(),
  hidden: z.boolean().optional(),
  sort: z.number().int().optional(),
  note: z.string().trim().max(200, '备注不超过 200 个字').nullish(),
});
export type CreateNetWorthItemRequest = z.infer<typeof CreateNetWorthItemRequestSchema>;

/**
 * `PATCH /api/net-worth-items/:id` —— 编辑。
 *
 * 🔴 **不发的键 = 不改**（与目标 / 重大资产同一约定）。
 * ⚠️ `kind` 允许改（把"资产"改成"负债"是合法的纠正），但**必须显式给**。
 */
export const UpdateNetWorthItemRequestSchema = z.object({
  kind: z.enum(KIND_VALUES).optional(),
  name: ItemNameSchema.optional(),
  amount: z.number().int('金额必须是整数分').nonnegative('金额不得为负').optional(),
  currency: CurrencySchema.optional(),
  ownerId: z.string().trim().min(1).optional(),
  hidden: z.boolean().optional(),
  sort: z.number().int().optional(),
  note: z.string().trim().max(200, '备注不超过 200 个字').nullable().optional(),
});
export type UpdateNetWorthItemRequest = z.infer<typeof UpdateNetWorthItemRequestSchema>;
